import {
  Euro,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ExternalLink,
  FileText,
  ArrowDownToLine,
  CalendarClock,
} from 'lucide-react';
import {
  useApprenantFinance,
  type FactureItem,
  type FactureStatus,
  type EncaissementItem,
  type RelanceItem,
} from '../hooks/useApprenantFinance.js';
import { CollapsibleSlice } from '../../../components/CollapsibleSlice.js';

interface Props {
  apprenantId: string;
}

/**
 * Slice FinancierCard — domaine Finance
 *
 * Pensé pour gérer les 4 workflows financement (CPF / OPCO / ST / Direct).
 * Le rendu adapte les KPIs et alertes selon `workflow`.
 *
 * KPIs toujours visible : Total / Encaissé / Reste / Workflow
 * Détail collapsible : factures, encaissements, relances, prochaine facturation
 */
export function FinancierCard({ apprenantId }: Props) {
  const { data: f, isLoading, error } = useApprenantFinance(apprenantId);

  if (isLoading || error || !f) {
    return (
      <CollapsibleSlice title="Financier" icon={<Euro className="text-primary-500 h-4 w-4" />}>
        <div className="text-sm text-neutral-500">{error?.message ?? 'Chargement...'}</div>
      </CollapsibleSlice>
    );
  }

  const pctEncaisse =
    f.montantTotalHT > 0 ? Math.round((f.montantEncaisseHT / f.montantTotalHT) * 100) : 0;
  const relancesUrgentes = f.relances.filter(
    (r) => r.urgence === 'urgente' || r.urgence === 'critique',
  ).length;

  return (
    <CollapsibleSlice
      title="Financier"
      icon={<Euro className="text-primary-500 h-4 w-4" />}
      badge={
        <span className="bg-primary-50 text-primary-700 rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
          {f.workflow}
        </span>
      }
      summary={
        <div className="space-y-2.5">
          {/* Barre de progression encaissé */}
          <div>
            <div className="mb-1 flex items-baseline justify-between text-xs">
              <span className="font-semibold text-neutral-700">Encaissé</span>
              <span className="text-neutral-500">
                <span className="text-base font-bold text-emerald-700">
                  {f.montantEncaisseHT.toLocaleString('fr-FR')} €
                </span>
                <span className="text-neutral-400">
                  {' '}
                  / {f.montantTotalHT.toLocaleString('fr-FR')} €
                </span>
                <span className="ml-1 font-semibold text-emerald-700">({pctEncaisse}%)</span>
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all"
                style={{ width: `${pctEncaisse}%` }}
              />
            </div>
          </div>

          {/* 3 KPIs */}
          <div className="grid grid-cols-3 gap-2">
            <Kpi
              label="Reste dû"
              value={`${f.resteDuHT.toLocaleString('fr-FR')} €`}
              color={f.resteDuHT > 0 ? 'text-amber-700' : 'text-emerald-700'}
            />
            <Kpi
              label="Factures"
              value={String(f.factures.length)}
              extra={`${f.factures.filter((x) => x.status === 'payée').length} payées`}
            />
            <Kpi
              label="Relances"
              value={String(f.relances.length)}
              color={relancesUrgentes > 0 ? 'text-rose-700' : 'text-neutral-700'}
              extra={relancesUrgentes > 0 ? `${relancesUrgentes} urgentes` : undefined}
            />
          </div>

          {/* Workflow info — toujours visible car critique */}
          <div className="border-primary-200 bg-primary-50 rounded-lg border p-2.5">
            <div className="text-primary-700 text-[10px] font-semibold uppercase tracking-wider">
              Workflow : {f.workflowLabel}
            </div>
            {f.prochaineFacturationDate && f.prochaineFacturationCondition && (
              <div className="mt-1 flex items-start gap-1.5 text-[11px] text-neutral-700">
                <CalendarClock className="text-primary-600 mt-0.5 h-3 w-3 flex-shrink-0" />
                <div>
                  <strong>Prochaine facturation</strong> :{' '}
                  {f.prochaineFacturationMontant?.toLocaleString('fr-FR')} € prévue le{' '}
                  {new Date(f.prochaineFacturationDate).toLocaleDateString('fr-FR')}
                  <div className="text-[10px] italic text-neutral-500">
                    {f.prochaineFacturationCondition}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      }
    >
      {/* Relances */}
      {f.relances.length > 0 && (
        <div className="mb-3">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
            Relances à faire ({f.relances.length})
          </div>
          <ul className="space-y-1.5">
            {f.relances.map((r, i) => (
              <RelanceRow key={i} relance={r} />
            ))}
          </ul>
        </div>
      )}

      {/* Factures */}
      <div className="mb-3">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
          Factures ({f.factures.length})
        </div>
        {f.factures.length === 0 ? (
          <div className="rounded-md border border-dashed border-neutral-200 p-3 text-center text-[11px] italic text-neutral-500">
            Aucune facture émise — facturation prévue en fin de formation (workflow CPF)
          </div>
        ) : (
          <ul className="space-y-1.5">
            {f.factures.map((fact) => (
              <FactureRow key={fact.id} facture={fact} />
            ))}
          </ul>
        )}
      </div>

      {/* Encaissements */}
      <div>
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
          Encaissements ({f.encaissements.length})
        </div>
        {f.encaissements.length === 0 ? (
          <div className="rounded-md border border-dashed border-neutral-200 p-3 text-center text-[11px] italic text-neutral-500">
            Aucun encaissement reçu — CPF paye après dépôt EDOF
          </div>
        ) : (
          <ul className="space-y-1">
            {f.encaissements.map((e) => (
              <EncaissementRow key={e.id} enc={e} />
            ))}
          </ul>
        )}
      </div>
    </CollapsibleSlice>
  );
}

// ────────────────────────────────────────────────────────────────────

function Kpi({
  label,
  value,
  extra,
  color = 'text-neutral-900',
}: {
  label: string;
  value: string;
  extra?: string;
  color?: string;
}) {
  return (
    <div className="rounded-md border border-neutral-200 bg-neutral-50 p-2 text-center">
      <div className="text-[9px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`mt-0.5 text-sm font-bold ${color}`}>{value}</div>
      {extra && <div className="text-[9px] text-neutral-500">{extra}</div>}
    </div>
  );
}

function FactureRow({ facture: f }: { facture: FactureItem }) {
  return (
    <li className="rounded-md border border-neutral-200 bg-white p-2">
      <div className="flex items-center gap-2">
        <FactureIcon status={f.status} />
        <span className="font-mono text-[11px] font-bold text-neutral-700">{f.numero}</span>
        <span className="flex-1 text-[10px] text-neutral-500">
          Émise le {new Date(f.date).toLocaleDateString('fr-FR')}
        </span>
        <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-700">
          {f.montantTTC.toLocaleString('fr-FR')} € TTC
        </span>
        {f.booksUrl && (
          <a
            href={f.booksUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-800"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <div className="ml-6 mt-0.5 flex items-center gap-3 text-[10px]">
        <span className="rounded px-1.5 py-0.5 font-medium">{statusLabel(f.status)}</span>
        <span className="text-neutral-500">
          Échéance :{' '}
          <span className="font-medium text-neutral-700">
            {new Date(f.echeance).toLocaleDateString('fr-FR')}
          </span>
        </span>
        {f.montantEncaisse > 0 && f.montantEncaisse < f.montantTTC && (
          <span className="text-amber-700">
            {f.montantEncaisse.toLocaleString('fr-FR')} € reçus
          </span>
        )}
        {f.retardJours && f.retardJours > 0 && (
          <span className="ml-auto font-semibold text-rose-700">⚠ Retard {f.retardJours}j</span>
        )}
      </div>
    </li>
  );
}

function FactureIcon({ status }: { status: FactureStatus }) {
  switch (status) {
    case 'payée':
      return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    case 'partiellement_payée':
      return <Clock className="h-4 w-4 text-amber-500" />;
    case 'en_retard':
      return <AlertTriangle className="h-4 w-4 text-rose-500" />;
    case 'envoyée':
      return <FileText className="text-primary-500 h-4 w-4" />;
    case 'brouillon':
      return <FileText className="h-4 w-4 text-neutral-400" />;
    case 'annulée':
      return <FileText className="h-4 w-4 text-neutral-300 line-through" />;
  }
}

function statusLabel(s: FactureStatus): string {
  return {
    payée: 'Payée',
    partiellement_payée: 'Partielle',
    en_retard: 'En retard',
    envoyée: 'Envoyée',
    brouillon: 'Brouillon',
    annulée: 'Annulée',
  }[s];
}

function EncaissementRow({ enc: e }: { enc: EncaissementItem }) {
  return (
    <li className="flex items-center gap-2 rounded px-2 py-1 text-xs hover:bg-neutral-50">
      <ArrowDownToLine className="h-3.5 w-3.5 text-emerald-500" />
      <span className="text-[10px] text-neutral-500">
        {new Date(e.date).toLocaleDateString('fr-FR')}
      </span>
      <span className="flex-1 truncate text-neutral-700">
        {e.source} {e.factureRef && <span className="text-neutral-400">· {e.factureRef}</span>}
      </span>
      <span className="text-[10px] text-neutral-400">{e.moyen}</span>
      <span className="font-semibold text-emerald-700">+{e.montant.toLocaleString('fr-FR')} €</span>
    </li>
  );
}

function RelanceRow({ relance: r }: { relance: RelanceItem }) {
  const urgenceColor =
    r.urgence === 'critique'
      ? 'border-rose-300 bg-rose-50'
      : r.urgence === 'urgente'
        ? 'border-amber-300 bg-amber-50'
        : 'border-neutral-200 bg-neutral-50';

  return (
    <li className={`rounded-md border p-2 ${urgenceColor}`}>
      <div className="flex items-center gap-2">
        <AlertTriangle
          className={`h-4 w-4 ${r.urgence === 'critique' ? 'text-rose-600' : 'text-amber-600'}`}
        />
        <span className="font-mono text-[11px] font-bold">{r.factureNumero}</span>
        <span className="flex-1 text-xs">
          {r.montant.toLocaleString('fr-FR')} € · retard {r.retardJours}j
        </span>
        <button className="text-primary-700 hover:bg-primary-50 rounded bg-white px-2 py-0.5 text-[10px] font-semibold shadow-sm">
          Relancer
        </button>
      </div>
      <div className="ml-6 mt-0.5 text-[10px] text-neutral-600">
        Échéance dépassée : {new Date(r.echeanceDepassee).toLocaleDateString('fr-FR')}
        {r.derniereRelance && (
          <span className="ml-2">
            · dernière relance : {new Date(r.derniereRelance).toLocaleDateString('fr-FR')}
          </span>
        )}
      </div>
    </li>
  );
}
